import { format, parseISO } from 'date-fns';
import { MONTH_DATE_LABEL } from '@src/utils/date-utils';
import {
  AmcNtbBlendedLtvRow,
  AmcNtbCohortLtvRow,
  AmcNtbDeliveryMetricsRow,
  CohortLtvMatrixRow,
  DeliveryMetricsL12MOverview,
  DeliveryMetricsRowWithMetrics
} from './amc-ntb-ltv-types';

export const formatShortMonth = (value: string): string =>
  format(parseISO(value), MONTH_DATE_LABEL);

export const withDeliveryMetricsCalculatedFields = (
  row: AmcNtbDeliveryMetricsRow
): DeliveryMetricsRowWithMetrics => ({
  ...row,
  costPerNtbAcquisition: row.ntbCustomers > 0 ? row.totalSpend / row.ntbCustomers : undefined,
  ntbAverageOrderValue: row.ntbOrders > 0 ? row.ntbTotalProductSales / row.ntbOrders : undefined,
  // Customer-based, so it pairs with totalCustomers -- not totalOrders, which would mix a
  // customer count with an order count.
  newToBrandPercent: row.totalCustomers > 0 ? row.ntbCustomers / row.totalCustomers : 0,
  newToBrandOrderPercent: row.totalOrders > 0 ? row.ntbOrders / row.totalOrders : 0
});

const TRAILING_MONTHS = 12;

// `rows` is sorted most-recent month first (see AmcNtbLtvResponse), so the trailing 12 months are
// simply its first 12 rows.
export const summarizeL12MDeliveryMetrics = (
  rows: AmcNtbDeliveryMetricsRow[]
): DeliveryMetricsL12MOverview => {
  const totals = rows.slice(0, TRAILING_MONTHS).reduce(
    (acc, row) => ({
      ntbCustomers: acc.ntbCustomers + row.ntbCustomers,
      totalSpend: acc.totalSpend + row.totalSpend,
      ntbOrders: acc.ntbOrders + row.ntbOrders,
      ntbTotalProductSales: acc.ntbTotalProductSales + row.ntbTotalProductSales
    }),
    { ntbCustomers: 0, totalSpend: 0, ntbOrders: 0, ntbTotalProductSales: 0 }
  );
  return {
    ntbCustomers: totals.ntbCustomers,
    costPerNtbAcquisition:
      totals.ntbCustomers > 0 ? totals.totalSpend / totals.ntbCustomers : undefined,
    ntbAverageOrderValue:
      totals.ntbOrders > 0 ? totals.ntbTotalProductSales / totals.ntbOrders : undefined
  };
};

export const getBlendedLtvAtMonthOffset = (
  rows: AmcNtbBlendedLtvRow[],
  monthOffset: number
): number | undefined =>
  rows.find(row => row.monthOffset === monthOffset)?.monthOffsetCumulativeLtv;

// bpa_cohort_ltv_report_sql.sql returns one row per (cohort_month, month_offset) — long format.
// This pivots it into one row per cohort with an offset-indexed array, for the cohort x
// month-offset matrix table. `maxMonthOffset` sets the array width regardless of which offsets
// are actually present, so every cohort row lines up under the same column headers; a
// (cohort_month, month_offset) pair outside that range is dropped rather than widening the array.
// Cohorts are returned most-recent first.
export const pivotCohortLtvRows = (
  rows: AmcNtbCohortLtvRow[],
  maxMonthOffset: number
): CohortLtvMatrixRow[] => {
  const byCohort = new Map<string, (number | null)[]>();
  rows.forEach(row => {
    if (row.monthOffset < 0 || row.monthOffset > maxMonthOffset) return;
    if (!byCohort.has(row.cohortMonth)) {
      byCohort.set(row.cohortMonth, new Array(maxMonthOffset + 1).fill(null));
    }
    byCohort.get(row.cohortMonth)![row.monthOffset] = row.cumulativeLtv;
  });
  return Array.from(byCohort.entries())
    .map(([cohortMonth, valuesByMonthOffset]) => ({ cohortMonth, valuesByMonthOffset }))
    .sort((a, b) => b.cohortMonth.localeCompare(a.cohortMonth));
};
